import React, { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { login, register } from '../api';
import { useAuthStore } from '../store';

interface AuthFormProps {
  mode: 'login' | 'register';
}

export function AuthForm({ mode }: AuthFormProps) {
  const navigate = useNavigate();
  const setUser = useAuthStore(state => state.setUser);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (mode === 'login') {
        const result = await login(email, password);
        setUser(result.user);
        navigate('/', { replace: true });
      } else {
        const result = await register(
          email,
          password,
          displayName || undefined,
        );
        setUser(result.user);
        navigate('/', { replace: true, state: { justRegistered: true } });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
      {/* Background image with blur and dark overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/textborne_concept_realistic_leatherbook.jpeg')" }}
      />
      <div className="absolute inset-0 backdrop-blur-sm bg-black/70" />
      
      {/* Content */}
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-400 mb-2 drop-shadow-lg">Textborne</h1>
          <p className="text-dark-100 text-lg drop-shadow">Reality Simulation Engine</p>
        </div>

        <div className="bg-dark-700/95 backdrop-blur-md rounded-lg p-8 shadow-2xl border border-dark-500/50">
          <h2 className="text-2xl font-semibold text-dark-50 mb-6">
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </h2>

          {error && (
            <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-dark-200 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="narrative-input w-full px-4 py-2"
                  placeholder="How should we call you?"
                />
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-dark-200 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="narrative-input w-full px-4 py-2"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-dark-200 mb-1">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="narrative-input w-full px-4 py-2"
                placeholder="••••••••"
                required
                minLength={mode === 'register' ? 8 : undefined}
              />
              {mode === 'register' && (
                <p className="text-dark-400 text-xs mt-1">At least 8 characters</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full py-3"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <span className="spinner w-5 h-5 mr-2" />
                  {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                mode === 'login' ? 'Sign In' : 'Create Account'
              )}
            </button>
            {mode === 'register' && (
              <p className="text-xs text-dark-400 text-center">
                Credits are required before generation. Purchases start at $5.
              </p>
            )}
          </form>

          <div className="mt-6 text-center text-dark-200">
            {mode === 'login' ? (
              <p>
                Don't have an account?{' '}
                <Link to="/register" className="text-primary-400 hover:text-primary-300">
                  Sign up
                </Link>
              </p>
            ) : (
              <p>
                Already have an account?{' '}
                <Link to="/login" className="text-primary-400 hover:text-primary-300">
                  Sign in
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
